import type { IPageAnalytic, IPageInfo } from '../types';
import type { IFilterSortOption } from '~/types/sort';

import { findCacheKeyByRequestId } from '~/modules/apps/websocket';
import { DEFAULT_PAGE_ANALYTIC_SORT_OPTIONS } from '../constants';
import { deepCopy } from './../../../utils/deepCopy';
import { create } from 'zustand';

export interface IPageAnalyticCacheValue {
  requestId?: string;
  data?: IPageAnalytic[];
}
export type IPageAnalyticDataCache = Record<string, IPageAnalyticCacheValue>;

export interface IPageAnalyticStoreState {
  cacheData: IPageAnalyticDataCache;
  pagesInfo: IPageInfo;
  progress: number;
  isShowBannerProgress: boolean;
  dataKeyErrors: any[];
  sortOptions: IFilterSortOption;
  setProgress: (progress: number) => void;
  upsertCacheData: (key: string, value: IPageAnalyticCacheValue) => void;
  getCacheData: () => IPageAnalyticDataCache;
  findCacheKeyByRequestId: (requestId: string) => string | undefined;
  setIsShowBannerProgress: (isShowBannerProgress: boolean) => void;
  setDataKeyErrors: (value: any[]) => void;
  checkAndDeleteDataKeyErrors: (value: any[]) => void;
  upsertPagesInfo: (value: IPageInfo) => void;
  getPagesInfo: () => IPageInfo;
  setSortOptions: (sortOptions: IFilterSortOption) => void;
}
export const usePageAnalyticStore = create<IPageAnalyticStoreState>()((set, get) => ({
  cacheData: {},
  pagesInfo: {},
  progress: 0,
  isShowBannerProgress: true,
  dataKeyErrors: [],
  sortOptions: DEFAULT_PAGE_ANALYTIC_SORT_OPTIONS,
  upsertCacheData: (key, value) => {
    const currentCampaigns = deepCopy(get().cacheData);
    const updatedCampaigns = {
      ...currentCampaigns,
      [key]: {
        requestId: currentCampaigns[key]?.requestId ?? '',
        ...value,
      },
    };

    set({ cacheData: updatedCampaigns });
  },
  getCacheData: () => get().cacheData,
  findCacheKeyByRequestId: (requestId) => findCacheKeyByRequestId(get().cacheData, requestId),
  setProgress: (progress) => set({ progress }),
  setIsShowBannerProgress: (isShowBannerProgress) => set({ isShowBannerProgress }),
  setDataKeyErrors: (value: any[]) => {
    const dataKeyErrors = deepCopy(get().dataKeyErrors);
    const isExisting =
      dataKeyErrors.findIndex((item) => JSON.stringify(item) === JSON.stringify(value)) > -1;
    !isExisting && dataKeyErrors.push(value);

    set({ dataKeyErrors });
  },
  checkAndDeleteDataKeyErrors: (value: any[]) => {
    const dataKeyErrors = deepCopy(get().dataKeyErrors);
    const isExisting =
      dataKeyErrors.findIndex((item) => JSON.stringify(item) === JSON.stringify(value)) > -1;

    isExisting && dataKeyErrors.splice(dataKeyErrors.indexOf(value), 1);

    set({ dataKeyErrors });
  },
  upsertPagesInfo: (newPageInfo) => {
    set({ pagesInfo: { ...get().pagesInfo, ...newPageInfo } });
  },
  getPagesInfo: () => get().pagesInfo,
  setSortOptions: (sortOptions) => set({ sortOptions }),
}));
